import { categories, allYears, directors } from "@/lib/films";
import type { FilterState, SortKey } from "@/lib/films";

type FilterPanelProps = {
  filters: FilterState;
  onChange: (patch: Partial<FilterState>) => void;
  onReset: () => void;
  resultCount: number;
};

const sortLabels: { key: SortKey; label: string }[] = [
  { key: "heat", label: "热度" },
  { key: "newest", label: "最新" },
  { key: "oldest", label: "最早" },
];

export default function FilterPanel({ filters, onChange, onReset, resultCount }: FilterPanelProps) {
  const years = allYears();

  return (
    <div className="flex flex-col gap-7">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-base font-bold text-foreground-950">筛选条件</h2>
        <button
          type="button"
          onClick={onReset}
          className="flex items-center gap-1 font-label text-xs text-foreground-500 transition-colors hover:text-accent-500"
        >
          <i className="ri-refresh-line" />
          重置
        </button>
      </div>

      <div>
        <p className="mb-3 font-label text-[11px] uppercase tracking-[0.2em] text-foreground-500">子分类</p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onChange({ category: "all" })}
            className={`whitespace-nowrap rounded-full border px-3 py-1.5 font-label text-xs transition-colors ${
              filters.category === "all"
                ? "border-accent-500 bg-accent-500 text-background-50"
                : "border-background-300 text-foreground-700 hover:border-foreground-500"
            }`}
          >
            全部
          </button>
          {categories.map((category) => (
            <button
              key={category.key}
              type="button"
              onClick={() => onChange({ category: category.key })}
              className={`whitespace-nowrap rounded-full border px-3 py-1.5 font-label text-xs transition-colors ${
                filters.category === category.key
                  ? "border-accent-500 bg-accent-500 text-background-50"
                  : "border-background-300 text-foreground-700 hover:border-foreground-500"
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="mb-3 font-label text-[11px] uppercase tracking-[0.2em] text-foreground-500">上映年份</p>
        <div className="relative">
          <select
            value={filters.year}
            onChange={(event) => onChange({ year: event.target.value })}
            aria-label="按上映年份筛选"
            className="h-10 w-full appearance-none rounded-md border border-background-300 bg-background-50 px-3 pr-9 font-label text-sm text-foreground-800 focus:border-accent-500 focus:outline-none"
          >
            <option value="all">全部年份</option>
            {years.map((year) => (
              <option key={year} value={String(year)}>
                {year} 年
              </option>
            ))}
          </select>
          <i className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-base text-foreground-500" />
        </div>
      </div>

      <div>
        <p className="mb-3 font-label text-[11px] uppercase tracking-[0.2em] text-foreground-500">导演</p>
        <div className="relative">
          <select
            value={filters.director}
            onChange={(event) => onChange({ director: event.target.value })}
            aria-label="按导演筛选"
            className="h-10 w-full appearance-none rounded-md border border-background-300 bg-background-50 px-3 pr-9 font-label text-sm text-foreground-800 focus:border-accent-500 focus:outline-none"
          >
            <option value="all">全部导演</option>
            {directors.map((director) => (
              <option key={director} value={director}>
                {director}
              </option>
            ))}
          </select>
          <i className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-base text-foreground-500" />
        </div>
      </div>

      <div>
        <p className="mb-3 font-label text-[11px] uppercase tracking-[0.2em] text-foreground-500">排序方式</p>
        <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1">
          {sortLabels.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => onChange({ sort: item.key })}
              className={`flex-1 whitespace-nowrap rounded-full px-3 py-1.5 font-label text-xs transition-colors ${
                filters.sort === item.key
                  ? "bg-primary-500 text-foreground-950"
                  : "text-foreground-600 hover:text-foreground-900"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-md border border-background-300 bg-background-50 p-4">
        <p className="font-label text-xs text-foreground-500">当前筛选结果</p>
        <p className="mt-1 font-heading text-2xl font-bold text-foreground-950">
          {resultCount}
          <span className="ml-1 font-label text-xs font-normal text-foreground-500">部影片</span>
        </p>
      </div>
    </div>
  );
}